import { useEffect, useLayoutEffect, useRef } from 'react';
import type { Key } from 'react-aria-components';

import type { DataTableLayout } from './DataTableLayout';
import { findVerticalScrollParent } from './findVerticalScrollParent';

type Options = {
  rowId: Key | undefined;
  hasRow: boolean;
  scrollContainer: 'self' | 'ancestor';
  layout: DataTableLayout | undefined;
  containerElement: HTMLElement | null;
  tableElement: HTMLElement | null;
  headerElement: HTMLElement | null;
  stickyHeaderOffset: number | undefined;
};

const MAX_SCROLLS = 4;
const MAX_FRAMES = 30;

const viewportTop = (element: HTMLElement) =>
  element === document.scrollingElement ? 0 : element.getBoundingClientRect().top;

const findRowElement = (table: HTMLElement, rowId: Key) =>
  table.querySelector<HTMLElement>(`[role="row"][data-key="${CSS.escape(String(rowId))}"]`);

export function useScrollToRow({
  rowId,
  hasRow,
  scrollContainer,
  layout,
  containerElement,
  tableElement,
  headerElement,
  stickyHeaderOffset,
}: Options) {
  const scrolledRowIdRef = useRef<Key | undefined>(undefined);
  const headerRef = useRef({ headerElement, stickyHeaderOffset });
  useLayoutEffect(() => {
    headerRef.current = { headerElement, stickyHeaderOffset };
  }, [headerElement, stickyHeaderOffset]);

  useEffect(() => {
    if (rowId === undefined || !hasRow || rowId === scrolledRowIdRef.current || !tableElement) {
      return;
    }
    scrolledRowIdRef.current = rowId;

    let frame = 0;
    let frames = 0;
    let scrolls = 0;
    let done = false;
    const next = () => {
      frames += 1;
      if (frames < MAX_FRAMES) {
        frame = requestAnimationFrame(step);
      } else {
        done = true;
      }
    };
    const step = () => {
      const header = headerRef.current;
      const headerHeight = header.headerElement?.getBoundingClientRect().height ?? 0;
      const offset = header.stickyHeaderOffset ?? 0;
      const scroller =
        scrollContainer === 'ancestor'
          ? containerElement && findVerticalScrollParent(containerElement)
          : layout
            ? tableElement
            : containerElement;

      if (layout && scrolls === 0) {
        const rowInfo = layout.getLayoutInfo(rowId);
        if (rowInfo && scroller) {
          const rowTop = tableElement.getBoundingClientRect().top - tableElement.scrollTop + rowInfo.rect.y;
          scroller.scrollTop += rowTop - viewportTop(scroller) - offset - headerHeight;
          scrolls += 1;
        }
        next();
        return;
      }

      const rowElement = findRowElement(tableElement, rowId);
      if (!scroller || !rowElement) {
        next();
        return;
      }
      const delta = rowElement.getBoundingClientRect().top - viewportTop(scroller) - offset - headerHeight;
      if (Math.abs(delta) > 1 && scrolls < MAX_SCROLLS) {
        scroller.scrollTop += delta;
        scrolls += 1;
        next();
        return;
      }
      done = true;
    };
    frame = requestAnimationFrame(step);

    return () => {
      cancelAnimationFrame(frame);
      if (!done) {
        scrolledRowIdRef.current = undefined;
      }
    };
  }, [containerElement, hasRow, layout, rowId, scrollContainer, tableElement]);
}
